import { useCallback, useRef, useState } from "react";
import { Loader2, ChevronDown, Check, User as UserIcon } from "lucide-react";

import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";

import api from "../../services/api";
import useListaDeAtendentes from "../../hooks/useListaDeAtendentes";

// Escolher para quem vai o atendimento.
//
// A janela abre já com quem está online — `online` vem do backend (presença
// cruzada com sessão ativa), não do `presenceStatus` cru, que fica preso em
// "online" depois do logout.
//
// Este arquivo é só a VISTA: busca, paginação e cancelamento moram em
// `useListaDeAtendentes`. Quem decide QUEM pode aparecer é o servidor, pela
// `fonte` — a lista de atendentes da empresa (padrão) ou os candidatos de uma
// chamada. Foi assim que sumiram o filtro repetido no navegador e o teto de
// ids que transformava 201 candidatos legítimos em erro na cara do operador.
//
// Usado pelas três janelas de transferir: conversa, chamada e ações em massa.

// O ponto é enfeite: quem navega por teclado ou usa leitor de tela precisa do
// TEXTO ao lado. Cor sozinha nunca foi informação.
export const PontoDePresenca = ({ online, className }) => (
	<span
		aria-hidden="true"
		className={cn(
			"h-2 w-2 shrink-0 rounded-full",
			// emerald-500 sobre o fundo claro fica perto de 2:1 — some para quem
			// enxerga pouco. O 600 passa no claro, o 400 passa no escuro.
			online ? "bg-emerald-600 dark:bg-emerald-400" : "bg-muted-foreground/40",
			className
		)}
	/>
);

// Fonte padrão: os atendentes da empresa. Sem termo, só quem está online.
export const atendentesDaEmpresa = async (
	{ searchParam, pageNumber },
	signal
) => {
	const { data } = await api.get("/users/", {
		params: {
			searchParam: searchParam || undefined,
			pageNumber,
			// Sem busca, a lista é de quem está no posto agora.
			onlyOnline: !searchParam,
			// Na busca, o campo `online` precisa vir junto para a linha dizer o
			// estado de cada um.
			includeOnline: Boolean(searchParam),
			// Quem está desativado ou em modo somente-leitura não atende:
			// oferecer essa gente é transferir para o vazio.
			onlyAtendentes: true,
		},
		signal,
	});

	return data;
};

const SeletorDeAtendente = ({
	aberto,
	valor,
	aoEscolher,
	placeholder = "Escolher atendente",
	rotuloLimpar,
	fonte = atendentesDaEmpresa,
	textoSemNinguem = "Ninguém online agora. Busque pelo nome para transferir mesmo assim.",
}) => {
	const buscaRef = useRef(null);
	// O popover é controlado: escolher alguém tem de FECHAR a lista. Sem o
	// estado aqui, o clique só tirava o foco e a lista continuava aberta por
	// cima do formulário — quem escolheu não via o que tinha escolhido.
	const [listaAberta, setListaAberta] = useState(false);

	const {
		busca,
		buscar,
		termo,
		items,
		total,
		hasMore,
		carregando,
		carregandoMais,
		carregarMais,
		erro,
		tentarDeNovo,
	} = useListaDeAtendentes({ aberto, fonte });

	const escolher = useCallback(
		(usuario) => {
			aoEscolher(usuario);
			setListaAberta(false);
		},
		[aoEscolher]
	);

	// "Online agora (20)" com 50 pessoas online era um número que mentia.
	const titulo = termo
		? "Resultado da busca"
		: total > items.length
			? `Online agora (${items.length} de ${total})`
			: `Online agora${total ? ` (${total})` : ""}`;

	const carregandoPrimeira = carregando && !carregandoMais;
	const vazio = !carregando && !items.length;

	return (
		<Popover
			open={listaAberta}
			onOpenChange={(o) => {
				setListaAberta(o);
				if (o) setTimeout(() => buscaRef.current?.focus(), 0);
			}}
		>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs focus:outline-hidden focus:ring-1 focus:ring-ring"
				>
					{valor ? (
						<PontoDePresenca online={valor.online} />
					) : (
						<UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
					)}
					<span
						className={cn(
							"flex-1 truncate text-left",
							!valor && "text-muted-foreground"
						)}
					>
						{valor ? valor.name : placeholder}
						{valor ? (
							<span className="sr-only">
								{valor.online ? " (online agora)" : " (offline)"}
							</span>
						) : null}
					</span>
					<ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-(--radix-popover-trigger-width) p-1"
				align="start"
			>
				<Input
					ref={buscaRef}
					placeholder="Buscar atendente (3 letras)"
					value={busca}
					onChange={(e) => buscar(e.target.value)}
					className="mb-1"
				/>

				<p className="px-2 py-1 text-xs font-medium text-muted-foreground">
					{titulo}
				</p>

				<div className="max-h-52 overflow-y-auto">
					{rotuloLimpar && valor && (
						<button
							type="button"
							onClick={() => escolher(null)}
							className="w-full rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
						>
							{rotuloLimpar}
						</button>
					)}

					{carregandoPrimeira && (
						<div className="flex items-center justify-center py-3">
							<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
						</div>
					)}

					{vazio && erro && (
						<p className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-sm text-destructive">
							Não foi possível carregar a lista.
							<button
								type="button"
								onClick={tentarDeNovo}
								className="text-xs font-medium text-primary hover:underline"
							>
								Tentar de novo
							</button>
						</p>
					)}

					{vazio && !erro && (
						<p className="px-2 py-1.5 text-sm text-muted-foreground">
							{termo ? "Nenhum atendente encontrado." : textoSemNinguem}
						</p>
					)}

					{items.map((opcao) => (
						<button
							type="button"
							key={opcao.id}
							onClick={() => escolher(opcao)}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
						>
							<Check
								className={cn(
									"h-3.5 w-3.5 shrink-0",
									valor?.id === opcao.id ? "opacity-100" : "opacity-0"
								)}
							/>
							<PontoDePresenca online={opcao.online} />
							<span className="flex-1 truncate">{opcao.name}</span>
							{/* O estado sempre tem texto: visível quando é "offline", que
							    muda a decisão de quem transfere, e só para leitor de tela
							    quando é "online" — repetir a palavra em toda linha viraria
							    ruído para quem enxerga. */}
							{opcao.online ? (
								<span className="sr-only">online agora</span>
							) : (
								<span className="shrink-0 text-xs text-muted-foreground">
									offline
								</span>
							)}
						</button>
					))}

					{/* A página que não veio não apaga a que veio: o aviso fica aqui,
					    embaixo da lista, com o clique para repetir a MESMA página. */}
					{erro && items.length > 0 && (
						<p className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-xs text-destructive">
							Não foi possível carregar o restante.
							<button
								type="button"
								onClick={tentarDeNovo}
								className="font-medium text-primary hover:underline"
							>
								Tentar de novo
							</button>
						</p>
					)}

					{/* Só os 20 primeiros vêm na primeira página: sem isto a janela
					    dizia "Online agora (20)" com 50 pessoas online, e as outras 30
					    não existiam para quem estava transferindo. */}
					{hasMore && !erro && (
						<button
							type="button"
							onClick={carregarMais}
							disabled={carregandoMais}
							className="flex w-full items-center justify-center gap-2 rounded-sm px-2 py-1.5 text-xs font-medium text-primary hover:bg-accent"
						>
							{carregandoMais ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : null}
							Carregar mais
						</button>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default SeletorDeAtendente;
